// src/components/modals/ReportIssueModal.js
import React, { useState, useEffect } from 'react';
import { XMarkIcon, FlagIcon } from '@heroicons/react/24/solid';
import GlassyIcon from '../common/GlassyIcon';
import Alert from '../common/Alert';
import ActionButton from '../common/ActionButton';
import { useAuth } from '../../firebase/useAuth';
import axios from 'axios';
import { backendURL } from '../../App';
import { OptimizedImage } from '../../utils/imageManager';

const ReportIssueModal = ({ onClose, item, style, onMouseDown, userInput }) => {
  const { userId, email } = useAuth();
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ message: '', type: '' });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setDescription('');
    setAlert({ message: '', type: '' });
    setLoading(false);
    setSubmitted(false);
  }, [item]);

  let itemContent = item?.word || item?.sentence || '';
  let itemLabel = item?.word ? 'Word' : 'Sentence';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      setAlert({
        message: 'Please provide a description of the issue.',
        type: 'error',
      });
      return;
    }
    setLoading(true);
    setAlert({ message: '', type: '' });

    try {
      await axios.post(`${backendURL}/sendEmail`, {
        itemId: item.id,
        type: itemLabel,
        itemContent: itemContent,
        description: description,
        userId: userId || 'Not logged in',
        userEmail: email || 'Not logged in',
        userInput: userInput || '',
      });
      setSubmitted(true);
    } catch (err) {
      setAlert({
        message: 'There was an error submitting your report. Please try again.',
        type: 'error',
      });
      console.error('Failed to submit issue report:', err);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="bg-background-secondary rounded-2xl shadow-2xl w-full max-w-lg border border-border border-t-4 flex flex-col"
        style={{ borderTopColor: 'var(--color-primary)', ...style }}
        onMouseDown={onMouseDown}
      >
        {/* Header */}
        <div
          className="relative p-4 sm:p-5 flex justify-between items-center overflow-hidden flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, var(--color-primary)15, var(--color-secondary)25)' }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-70 dark:from-white/5 dark:opacity-40" />
          <div className="relative z-10 flex items-center">
            <GlassyIcon icon={FlagIcon} size="sm" color="orange" className="mr-3" />
            <h3 className="text-lg font-bold text-content-primary">Report an Issue</h3>
          </div>
          <button onClick={onClose} className="relative z-10 p-1 rounded-full text-content-secondary hover:bg-background-primary transition-colors">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Success body */}
        <div className="flex flex-col items-center justify-center px-6 py-10 space-y-4 text-center">
          <div
            className="w-28 h-28 rounded-full flex items-center justify-center overflow-hidden"
            style={{ background: 'var(--color-background-primary)', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}
          >
            <OptimizedImage
              imageKey="ACTION_CORRECT"
              className="w-20 h-20 object-contain"
              fallback={<span className="text-4xl">✅</span>}
            />
          </div>
          <h4 className="text-xl font-bold text-content-primary">Report Submitted!</h4>
          <p className="text-sm text-content-secondary">Thank you for your feedback. We&apos;ll look into it.</p>
        </div>

        {/* Footer */}
        <div
          className="px-4 sm:px-6 py-3 sm:py-4 flex justify-end border-t border-border flex-shrink-0"
          style={{ background: 'var(--color-background-primary)' }}
        >
          <ActionButton variant="glassy" type="button" onClick={onClose} color="orange" className="w-full sm:w-auto px-4 py-2 text-white">
            Close
          </ActionButton>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-background-secondary rounded-2xl shadow-2xl w-full max-w-lg border border-border border-t-4 flex flex-col max-h-[90vh] sm:max-h-[80vh]"
      style={{
        borderTopColor: 'var(--color-primary)',
        ...style,
      }}
      onMouseDown={onMouseDown}
    >
      {/* Glassmorphic header */}
      <div
        className="relative p-4 sm:p-5 flex justify-between items-center overflow-hidden flex-shrink-0"
        style={{
          background: 'linear-gradient(135deg, var(--color-primary)15, var(--color-secondary)25)',
        }}
      >
        {/* Glassmorphic overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-70 dark:from-white/5 dark:opacity-40" />

        {/* Content */}
        <div className="relative z-10 flex items-center">
          <GlassyIcon
            icon={FlagIcon}
            size="sm"
            color="orange"
            className="mr-3"
          />
          <h3 className="text-lg font-bold text-content-primary">Report an Issue</h3>
        </div>

        <button onClick={onClose} className="relative z-10 p-1 rounded-full text-content-secondary hover:bg-background-primary transition-colors">
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-3">
          {/* Character image with glassmorphic background */}
          <div className="text-center">
            <div className="relative inline-block mb-4">
              <div
                className="
                  w-28 h-28 rounded-full backdrop-blur-sm border border-border
                  flex items-center justify-center overflow-hidden mx-auto
                "
                style={{
                  background: 'var(--color-background-primary)',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                }}
              >
                {/* Subtle glassmorphic overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30 rounded-full" />
                <OptimizedImage
                  imageKey="ACTION_REPAIR"
                  className="relative z-10 w-20 h-20 object-contain"
                  fallback={<span className="text-4xl">🔧</span>}
                />
              </div>
            </div>
          </div>

          {alert.message && <Alert message={alert.message} type={alert.type} />}

          {/* Item being reported */}
          <div
            className="
              relative p-4 rounded-2xl backdrop-blur-sm border border-border 
              overflow-hidden
            "
            style={{
              background: 'linear-gradient(135deg, var(--color-accent)08, var(--color-accent)15)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            }}
          >
            {/* Glassmorphic overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30" />

            {/* Content */}
            <div className="relative z-10">
              <label className="block text-sm font-semibold text-content-secondary uppercase tracking-wider mb-2">{itemLabel} being reported</label>
              <div className="text-base text-content-primary font-medium">
                {itemContent} <span className="text-xs text-content-secondary">({item.id})</span>
              </div>
            </div>
          </div>

          {/* User Input Display (if provided) */}
          {userInput && userInput.trim() && (
            <div
              className="relative p-3 rounded-2xl backdrop-blur-sm border border-border overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, var(--color-primary)08, var(--color-primary)15)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30" />
              <div className="relative z-10">
                <label className="block text-xs font-semibold text-content-secondary uppercase tracking-wider mb-1">Your input</label>
                <div className="text-sm text-content-primary font-medium font-mono bg-background-primary rounded-lg px-3 py-2 border border-border">
                  {userInput}
                </div>
              </div>
            </div>
          )}

          {/* Description textarea */}
          <div>
            <label htmlFor="description" className="block text-xs font-semibold text-content-secondary uppercase tracking-wider mb-1">
              Describe the issue
            </label>
            <div
              className="relative rounded-2xl backdrop-blur-sm border-2 border-border overflow-hidden transition-all duration-200 focus-within:border-primary focus-within:shadow-lg"
              style={{
                background: 'var(--color-background-primary)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-30 dark:from-white/5 dark:opacity-20" />
              <textarea
                id="description"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., The audio doesn't match, a translation is incorrect..."
                className="relative z-10 w-full px-3 py-2 bg-transparent resize-none focus:outline-none focus:ring-0 focus:border-transparent outline-none border-none text-content-primary placeholder-content-secondary/60 transition-all duration-200"
                style={{ outline: 'none', border: 'none', boxShadow: 'none' }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-4 sm:px-6 py-3 sm:py-4 flex justify-end space-x-4 border-t border-border flex-shrink-0"
          style={{
            background: 'var(--color-background-primary)',
          }}
        >
          <ActionButton
            variant="glassy"
            type="button"
            onClick={onClose}
            color="neutral"
            className="w-full sm:w-auto px-4 py-2 text-white"
          >
            Cancel
          </ActionButton>

          <ActionButton
            variant="glassy"
            type="submit"
            form="report-issue-form"
            color="orange"
            className="w-full sm:w-auto px-4 py-2 text-white"
            loading={loading}
            disabled={!description || loading}
          >
            Submit Report
          </ActionButton>
        </div>
      </form>
    </div>
  );
};

export default ReportIssueModal;